import { useEffect, useState } from 'react';

/**
 * useMapAreaPortalTarget — resolve + STABILIZE the `.deck-map-area` portal host
 * for map-viewport modals (MapCombatPanel, DiamondMapModal, CramMapModal, ...).
 *
 * Why this exists (combat-modal flicker, 2026-07-11):
 * The naive wrappers used to resolve their portal target synchronously on every
 * render as `document.querySelector('.deck-map-area') || document.body`. When a
 * single render lands while `.deck-map-area` is momentarily absent (a Dashboard
 * branch flip to a processing/loading state, a mode toggle, a keyed map swap, or
 * a refetch frame), the target falls back to <body>; the very next render
 * re-parents it back into `.deck-map-area`. React's createPortal reconciler
 * unmounts the whole panel subtree from the old container and remounts it into
 * the new one — which RECREATES the DOM and replays the modal's CSS entry
 * keyframes (scale 0.98 -> 1 + fade of the blurred glass panel). That re-pop is
 * the "every so often" flash.
 *
 * This hook mirrors the hardened pattern already blessed in BossMapModal /
 * GameViewportModal, with one extra guard: once we've landed on a live
 * `.deck-map-area`, we KEEP it and never downgrade to <body> on a stray absent
 * frame. If the pinned node genuinely leaves the DOM (a real remount), we
 * re-resolve to the new one. `setTarget` returns the previous reference when
 * nothing changed, so a stable session produces no extra renders and no loop.
 *
 * @param {boolean} active - gate resolution (pass a truthy render-gate, e.g. !!node)
 * @returns {Element|null} the portal host, or null until first resolved
 */
export default function useMapAreaPortalTarget(active = true) {
  const [target, setTarget] = useState(null);

  // No dependency array on purpose: the resolver runs after every commit so it
  // can recover if `.deck-map-area` is remounted mid-session. Each run is a
  // cheap querySelector, and setTarget no-ops (returns prev) while the target is
  // stable, so there is no render loop.
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    let cancelled = false;
    let attempts = 0;
    let raf;

    const resolve = () => {
      if (cancelled) return;
      const el = document.querySelector('.deck-map-area') || document.body;
      setTarget((prev) => {
        // Keep a still-attached `.deck-map-area` rather than downgrading to
        // <body> on a stray frame where the query momentarily misses — this is
        // what kills the re-parent flash.
        if (prev && prev !== document.body && document.body.contains(prev)) {
          return prev;
        }
        return el;
      });
      // Only keep retrying (up to ~1s of frames) while we're stuck on <body>, so
      // we upgrade to `.deck-map-area` as soon as the dashboard mounts it.
      if (el === document.body && attempts < 60) {
        attempts += 1;
        raf = requestAnimationFrame(resolve);
      }
    };

    resolve();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  });

  return target;
}
